import { ApiClientsError } from '#pplx/frontend/api-clients/core/errors.js';
import { MAX_RETRIES } from '#pplx/frontend/api-clients/core/utils.js';
import { getSignedUrlStaleTime, recordSignedUrlFetch, } from '#pplx/frontend/apps/ask/all-results/hooks/internal/signedUrlRefresh.js';
import { getAllResultsQueryKey, getBackgroundEntriesQueryKey, getFirstEntryQueryKey, getLatestEntryQueryKey, getPaginationStateQueryKey, INITIAL_PAGINATED_FETCH_SIZE, MAX_CONSECUTIVE_BACKGROUND_ONLY_PAGES, PAGINATED_FETCH_SIZE, isEmptyThreadPage, mergeThreadPage, } from '#pplx/frontend/apps/ask/all-results/hooks/internal/threadPagination.js';
import { getThreadByUUID } from '#pplx/frontend/apps/ask/queries/entry-queries/getThreadByUUID.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { prefetchPersistedQuery } from '#pplx/frontend/react-query/prefetchQuery.js';
import { QueryClientHelper } from '#pplx/frontend/react-query/QueryClientHelper.js';
const INITIAL_FETCH_SIZE = 10;
const RECURSIVE_FETCH_SIZE = INITIAL_FETCH_SIZE * 10;
const FETCH_LIMIT = RECURSIVE_FETCH_SIZE * 10 + INITIAL_FETCH_SIZE;
export const THREAD_METADATA_QUERY_KEY = 'thread_metadata';
export const THREAD_PARTIAL_LOAD_QUERY_KEY = 'thread_partial_load';
export const THREAD_PRELOAD_STATE_QUERY_KEY = 'thread_preload_state';
export function resolveSearchThreadQueryMode(locationHash) {
    return {
        paginationEnabled: locationHash.length <= 1,
        paginateFromLatest: true,
    };
}
export function getAllResultsPartialLoadQueryKey(slugOrUUID, paginationEnabled, paginateFromLatest) {
    const paginationPosition = paginateFromLatest ? 'start' : 'end';
    return QueryClientHelper.makeEphemeralQueryKey(THREAD_PARTIAL_LOAD_QUERY_KEY, slugOrUUID, paginationEnabled ? paginationPosition : undefined);
}
export function getAllResultsPreloadStateQueryKey(slugOrUUID, paginationEnabled, paginateFromLatest) {
    const paginationPosition = paginateFromLatest ? 'start' : 'end';
    return QueryClientHelper.makeEphemeralQueryKey(THREAD_PRELOAD_STATE_QUERY_KEY, slugOrUUID, paginationEnabled ? paginationPosition : undefined);
}
const unreadThreadOpens = new Set();
const threadReadAt = new Map();
export function recordUnreadThreadOpen(contextUUID) {
    unreadThreadOpens.add(contextUUID);
}
export function recordThreadRead(contextUUID) {
    unreadThreadOpens.delete(contextUUID);
    threadReadAt.set(contextUUID, Date.now());
}
export function consumeUnreadThreadOpen(contextUUID) {
    return !!contextUUID && unreadThreadOpens.delete(contextUUID);
}
export function setThreadMetadataInCache(queryClient, slugOrUUID, metadata) {
    queryClient.setQueryData(QueryClientHelper.makeQueryKey(THREAD_METADATA_QUERY_KEY, slugOrUUID), (prev) => ({
        ...prev,
        ...metadata,
    }));
}
export function getAllResultsQueryOptions({ queryClient, slugOrUUID, paginationEnabled, paginateFromLatest, initialPaginatedFetchSize = INITIAL_PAGINATED_FETCH_SIZE, supportedBlockUseCases, reason, isSidecar, lifecycle, }) {
    const paginationPosition = paginateFromLatest ? 'start' : 'end';
    const activePaginationPosition = paginationEnabled ? paginationPosition : undefined;
    const queryKey = getAllResultsQueryKey(slugOrUUID, activePaginationPosition);
    const backgroundQueryKey = getBackgroundEntriesQueryKey(slugOrUUID, activePaginationPosition);
    const paginationStateQueryKey = getPaginationStateQueryKey(slugOrUUID, paginationPosition);
    const partialLoadQueryKey = getAllResultsPartialLoadQueryKey(slugOrUUID, paginationEnabled, paginateFromLatest);
    return {
        queryKey,
        staleTime: getSignedUrlStaleTime,
        retry: (failureCount, error) => {
            return (error instanceof ApiClientsError && error.status === 403 && isSidecar && failureCount < 2);
        },
        queryFn: async () => {
            const accumulatedEntries = [];
            const accumulatedBackgroundEntries = [];
            const cachedEntries = paginationEnabled
                ? queryClient.getQueryData(queryKey)
                : undefined;
            const cachedBackgroundEntries = paginationEnabled
                ? queryClient.getQueryData(backgroundQueryKey)
                : undefined;
            const cachedPaginationState = paginationEnabled
                ? queryClient.getQueryData(paginationStateQueryKey)
                : undefined;
            let cursor = null;
            let isFirstPage = true;
            const seenCursors = new Set();
            let visibleEntries = cachedEntries ?? accumulatedEntries;
            const fetchedAt = Date.now();
            queryClient.setQueryData(partialLoadQueryKey, false);
            lifecycle?.onQueryStarted?.();
            if (paginationEnabled && !cachedPaginationState) {
                queryClient.setQueryData(paginationStateQueryKey, {
                    nextCursor: null,
                    boundaryEntryUuid: undefined,
                    backgroundBoundaryEntryUuid: undefined,
                });
            }
            for (let pageCount = 0; !paginationEnabled || pageCount < MAX_CONSECUTIVE_BACKGROUND_ONLY_PAGES; pageCount += 1) {
                let results;
                if (isFirstPage)
                    lifecycle?.onFirstPageStarted?.();
                const requestedAt = Date.now();
                try {
                    results = await getThreadByUUID({
                        slugOrUUID,
                        options: {
                            withParentInfo: true,
                            supportedBlockUseCases,
                            limit: paginationEnabled
                                ? isFirstPage
                                    ? initialPaginatedFetchSize
                                    : PAGINATED_FETCH_SIZE
                                : isFirstPage
                                    ? INITIAL_FETCH_SIZE
                                    : RECURSIVE_FETCH_SIZE,
                            fromFirst: !paginateFromLatest,
                            withFirstEntry: paginationEnabled && paginateFromLatest && isFirstPage,
                            withLatestEntry: paginationEnabled && !paginateFromLatest && isFirstPage,
                            cursor,
                        },
                        numRetries: MAX_RETRIES,
                        reason,
                    });
                }
                catch (error) {
                    if (isFirstPage || visibleEntries.length === 0) {
                        if (isFirstPage)
                            lifecycle?.onFirstPageException?.();
                        throw error;
                    }
                    logger.error(new Error('Thread pagination failed mid-load'), {
                        slugOrUUIDLength: slugOrUUID.length,
                        loadedEntries: accumulatedEntries.length,
                        failedPage: isFirstPage
                            ? 0
                            : Math.ceil(accumulatedEntries.length / RECURSIVE_FETCH_SIZE),
                        error,
                    });
                    if (paginationEnabled) {
                        // Resume from the failed cursor instead of treating the thread as fully loaded.
                        queryClient.setQueryData(paginationStateQueryKey, {
                            nextCursor: cursor ?? null,
                            boundaryEntryUuid: accumulatedEntries.at(-1)?.backend_uuid,
                            backgroundBoundaryEntryUuid: accumulatedBackgroundEntries.at(-1)?.backend_uuid,
                        });
                    }
                    queryClient.setQueryData(partialLoadQueryKey, true);
                    lifecycle?.onPartialLoad?.();
                    break;
                }
                if (isFirstPage)
                    lifecycle?.onFirstPageResponse?.(results?.status === 'success');
                if (isFirstPage && results?.thread_metadata) {
                    queryClient.setQueryData(QueryClientHelper.makeQueryKey(THREAD_METADATA_QUERY_KEY, slugOrUUID), results.thread_metadata);
                    const contextUUID = (results.entries[0] ?? results.background_entries?.[0])?.context_uuid;
                    if (results.thread_metadata.unread &&
                        contextUUID &&
                        (threadReadAt.get(contextUUID) ?? 0) < requestedAt) {
                        recordUnreadThreadOpen(contextUUID);
                    }
                }
                // A page can contain only background entries and still have a cursor; only a page with
                // no entries of either kind ends the thread.
                if (results?.status !== 'success' || isEmptyThreadPage(results)) {
                    if (paginationEnabled && (!cachedPaginationState || !isFirstPage)) {
                        queryClient.setQueryData(paginationStateQueryKey, {
                            nextCursor: null,
                            boundaryEntryUuid: accumulatedEntries.at(-1)?.backend_uuid,
                            backgroundBoundaryEntryUuid: accumulatedBackgroundEntries.at(-1)?.backend_uuid,
                        });
                    }
                    break;
                }
                if (isFirstPage && paginationEnabled) {
                    queryClient.setQueryData(paginateFromLatest
                        ? getFirstEntryQueryKey(slugOrUUID)
                        : getLatestEntryQueryKey(slugOrUUID), (paginateFromLatest ? results.first_entry : results.latest_entry) ?? null);
                }
                const newEntries = results.entries;
                if (paginateFromLatest)
                    accumulatedEntries.unshift(...newEntries);
                else
                    accumulatedEntries.push(...newEntries);
                const newBackground = results.background_entries ?? [];
                if (newBackground.length) {
                    if (paginateFromLatest)
                        accumulatedBackgroundEntries.unshift(...newBackground);
                    else
                        accumulatedBackgroundEntries.push(...newBackground);
                    const visibleBackgroundEntries = paginationEnabled && cachedBackgroundEntries?.length
                        ? mergeThreadPage(cachedBackgroundEntries, accumulatedBackgroundEntries, {
                            position: paginateFromLatest ? 'end' : 'start',
                            boundaryEntryUuid: undefined,
                        })
                        : [...accumulatedBackgroundEntries];
                    queryClient.setQueryData(backgroundQueryKey, visibleBackgroundEntries);
                }
                visibleEntries =
                    paginationEnabled && cachedEntries?.length
                        ? mergeThreadPage(cachedEntries, accumulatedEntries, {
                            position: paginateFromLatest ? 'end' : 'start',
                            boundaryEntryUuid: undefined,
                        })
                        : [...accumulatedEntries];
                queryClient.setQueryData(queryKey, visibleEntries);
                const responseCursor = results.next_cursor ?? null;
                const nextCursor = responseCursor && seenCursors.has(responseCursor) ? null : responseCursor;
                if (nextCursor)
                    seenCursors.add(nextCursor);
                if (paginationEnabled) {
                    queryClient.setQueryData(paginationStateQueryKey, {
                        nextCursor,
                        boundaryEntryUuid: accumulatedEntries.at(-1)?.backend_uuid,
                        backgroundBoundaryEntryUuid: accumulatedBackgroundEntries.at(-1)?.backend_uuid,
                    });
                    if (newEntries.length > 0)
                        break;
                }
                else if (accumulatedEntries.length + accumulatedBackgroundEntries.length >= FETCH_LIMIT) {
                    // The backend's page size covers both foreground and background entries.
                    break;
                }
                if (!nextCursor)
                    break;
                cursor = nextCursor;
                isFirstPage = false;
            }
            // Cached older pages are not re-signed by this refetch, so only this run's entries count.
            recordSignedUrlFetch(queryClient, queryKey, [...accumulatedEntries, ...accumulatedBackgroundEntries], fetchedAt);
            return visibleEntries;
        },
    };
}
export function preloadAllResultsQuery(params) {
    return prefetchPersistedQuery({
        options: getAllResultsQueryOptions(params),
        queryClient: params.queryClient,
        allowPersist: params.allowPersist,
    });
}
//# sourceMappingURL=allResultsQuery.js.map